import { useEffect, useState } from 'react';
import { getTrainingAdminCourses, GetTrainingAdminCoursesOutputType, saveTrainingCourse } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Plus, Search, BookOpen, Users, ChevronRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type Course = GetTrainingAdminCoursesOutputType['courses'][0];

export default function CoursesSection({ onEditCourse }: { onEditCourse: (id: string) => void }) {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newCode, setNewCode] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getTrainingAdminCourses({ statusFilter }).then(d => setCourses(d.courses)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter]);

  const filtered = courses.filter(c =>
    c.title.toLowerCase().includes(search.toLowerCase()) ||
    (c.courseCode || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setSaving(true);
    try {
      const result = await saveTrainingCourse({ title: newTitle, courseCode: newCode || undefined });
      toast.success('Course created');
      setShowCreate(false);
      setNewTitle('');
      setNewCode('');
      onEditCourse(result.id);
    } catch { toast.error('Failed to create course'); }
    setSaving(false);
  };

  if (loading) {
    return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-foreground">Courses</h2>
        <Button size="sm" onClick={() => setShowCreate(true)}><Plus className="w-4 h-4 mr-1" /> New Course</Button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input placeholder="Search courses…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Draft">Draft</SelectItem>
            <SelectItem value="Published">Published</SelectItem>
            <SelectItem value="Archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No courses found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(c => (
            <button key={c.id} onClick={() => onEditCourse(c.id)} className="w-full flex items-center gap-4 p-4 bg-card border border-border rounded-lg hover:border-primary/30 transition-colors text-left group">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="font-medium text-foreground truncate">{c.title}</p>
                  <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${c.status === 'Published' ? 'bg-primary/10 text-primary' : c.status === 'Archived' ? 'bg-muted text-muted-foreground' : 'bg-muted text-muted-foreground'}`}>
                    {c.status || 'Draft'}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  {c.courseCode && <span>{c.courseCode}</span>}
                  <span>{c.moduleCount} modules</span>
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{c.enrollmentCount} enrolled</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/train/admin/builder/${c.id}`); }}
                  className="p-1.5 rounded-md hover:bg-primary/10 text-primary opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Interactive Builder"
                >
                  <Sparkles className="w-4 h-4" />
                </button>
                <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Course</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Course Title</Label><Input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="e.g. Front of House – Bar" /></div>
            <div><Label>Course Code (optional)</Label><Input value={newCode} onChange={e => setNewCode(e.target.value)} placeholder="e.g. FOH-BAR-001" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button disabled={!newTitle.trim() || saving} onClick={handleCreate}>{saving ? 'Creating…' : 'Create Course'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
